import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { setupService } from "../../../Core/Api/services";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { YoutubeChannel } from "../../../Core/Api/Types/youtube-channel";
import { produce } from "immer";
import { TranskodeYoutubeChannelDeliveryPolicy } from "../../../Core/Api/Types/transkode-youtube-channel-delivery";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdateYoutubeChannelElementProps = {
  mode: "create" | "update" | "delete";
  channels: YoutubeChannel[];
  channelId: number | null;
  onConfirm: () => void;
};

export type ModalCreateOrUpdateYoutubeChannelProps = CommonModalProps<ModalCreateOrUpdateYoutubeChannelElementProps>;

const ModalCreateOrUpdateYoutubeChannel = React.forwardRef<any, ModalCreateOrUpdateYoutubeChannelProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { mode, channels, channelId, closeModal, onConfirm } = props;
    const enqueueSnackbar = useCustomSnackbar();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [ytChannel, setYtChannel] = useState<YoutubeChannel>(
      mode === "update" || mode === "delete"
        ? channels.find((x) => x.channelId === channelId)!
        : new YoutubeChannel({
            name: "",
            channelId: "",
            description: "",
            adBreakTime: 0,
            preRoll: false,
            endRoll: false,
            blockOutsideOwnership: true,
            isMadeForKids: false,
            notifySubscribers: false,
            usagePolicy: TranskodeYoutubeChannelDeliveryPolicy.None,
          })
    );

    const formId = useMemo(() => "modal-create-or-update-youtube-channel", []);
    const isFormValid = useAppSelector(GetFormIsValid(formId));

    const handleConfirmClick = useCallback(() => {
      setIsLoading(true);
      let request: YoutubeChannel[] = channels;
      if (mode === "create") request.push(ytChannel);
      else if (mode === "update") {
        request = request.filter((x) => x.channelId !== channelId);
        request.push(ytChannel);
      } else {
        request = request.filter((x) => x.channelId !== channelId);
      }

      setupService.UpdateYoutubeChannelsInfos(request).then((response) => {
        if (response && response.success) {
          enqueueSnackbar(
            t("alert:success.youtubeChannelSuccess", {
              context: mode === "create" ? "created" : mode === "update" ? "updated" : "deleted",
            }),
            "success"
          );
          onConfirm && onConfirm();
          setIsLoading(false);
          closeModal && closeModal();
        } else enqueueSnackbar("Oops! " + response.message, "error");
      });
    }, [ytChannel]);

    const handleCancelClick = () => {
      closeModal && closeModal();
    };

    return (
      <Modal
        title={t("modal:title.youtubeChannelAction", {
          context: mode === "create" ? "create" : mode === "update" ? "update" : "delete",
          channelName: ytChannel?.name,
        })}
        reference={ref}
        closeModal={handleCancelClick}
        width={800}
        footer={
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
            <Button
              color={mode === "delete" ? "error" : "success"}
              variant="outlined"
              disabled={!isFormValid && mode !== "delete"}
              onClick={handleConfirmClick}
            >
              {isLoading ? (
                <CircularProgress size={20} thickness={5} />
              ) : mode === "create" ? (
                t("common:create")
              ) : mode === "update" ? (
                t("common:update")
              ) : (
                t("common:delete")
              )}
            </Button>
          </Stack>
        }
      >
        {mode !== "delete" ? (
          <Stack spacing={1}>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <FormInputText
                  label={t("common:name")}
                  placeholder="name"
                  validationProps={{
                    formId: formId,
                    fieldId: "name",
                    required: true,
                    minLength: 5,
                  }}
                  value={ytChannel?.name}
                  onChange={(event) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.name = event.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <FormInputText
                  label="Id"
                  placeholder="id"
                  validationProps={{
                    formId: formId,
                    fieldId: "id",
                    required: true,
                    minLength: 5,
                  }}
                  value={ytChannel?.channelId}
                  onChange={(event) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.channelId = event.target.value;
                      })
                    )
                  }
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={12}>
                <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
                <OutlinedInput
                  placeholder={t("common:description")}
                  sx={{ width: "100%" }}
                  onChange={(event) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.description = event.target.value;
                      })
                    )
                  }
                  value={ytChannel?.description}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.usagePolicy")}</InputLabel>
                <Autocomplete
                  options={Object.keys(TranskodeYoutubeChannelDeliveryPolicy)}
                  value={ytChannel?.usagePolicy}
                  onChange={(e, value) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.usagePolicy = value as TranskodeYoutubeChannelDeliveryPolicy;
                      })
                    )
                  }
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("common:playlistId")}</InputLabel>
                <Autocomplete
                  options={[]}
                  multiple
                  freeSolo
                  value={ytChannel?.playlistId || []}
                  onChange={(e, value) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.playlistId = value as string[];
                      })
                    )
                  }
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.enterPlaylistIds")}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("common:adBreakTime")}</InputLabel>
                <OutlinedInput
                  type="number"
                  placeholder={t("common:adBreakTime")}
                  sx={{ width: "100%" }}
                  onChange={(event) => {
                    if (Number(event.target.value) < 0) return;
                    if (Number(event.target.value) === 0)
                      setYtChannel(
                        produce((draft) => {
                          if (draft) {
                            draft.preRoll = false;
                            draft.endRoll = false;
                          }
                        })
                      );

                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.adBreakTime = Number(event.target.value);
                      })
                    );
                  }}
                  value={ytChannel?.adBreakTime}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("common:ownership")}</InputLabel>
                <Autocomplete
                  options={[]}
                  multiple
                  freeSolo
                  value={ytChannel?.ownership || []}
                  onChange={(e, value) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.ownership = value as string[];
                      })
                    )
                  }
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.enterTerritoryCodes")}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("common:keywords")}</InputLabel>
                <Autocomplete
                  options={[]}
                  multiple
                  freeSolo
                  value={ytChannel?.keywords || []}
                  onChange={(event, value) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.keywords = value as string[];
                      })
                    )
                  }
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.enterVideoKeywords")}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.assetLabels")}</InputLabel>
                <Autocomplete
                  options={[]}
                  multiple
                  freeSolo
                  value={ytChannel?.assetLabels || []}
                  onChange={(event, value) =>
                    setYtChannel(
                      produce((draft) => {
                        if (draft) draft.assetLabels = value as string[];
                      })
                    )
                  }
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                      placeholder={t("field:placeholder.assetLabels")}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={ytChannel?.preRoll ?? false}
                      disabled={
                        ytChannel?.adBreakTime === null ||
                        ytChannel?.adBreakTime === undefined ||
                        ytChannel?.adBreakTime < 1
                      }
                      onChange={(e) =>
                        setYtChannel(
                          produce((draft) => {
                            if (draft) draft.preRoll = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("common:preRoll")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={ytChannel?.endRoll ?? false}
                      disabled={
                        ytChannel?.adBreakTime === null ||
                        ytChannel?.adBreakTime === undefined ||
                        ytChannel?.adBreakTime < 1
                      }
                      onChange={(e) =>
                        setYtChannel(
                          produce((draft) => {
                            if (draft) draft.endRoll = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("common:endRoll")}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={ytChannel?.notifySubscribers ?? false}
                      onChange={(e) =>
                        setYtChannel(
                          produce((draft) => {
                            if (draft) draft.notifySubscribers = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("common:notifySubscribers")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={ytChannel?.blockOutsideOwnership ?? false}
                      onChange={(e) =>
                        setYtChannel(
                          produce((draft) => {
                            if (draft) draft.blockOutsideOwnership = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("common:blockOutsideOwnership")}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={ytChannel?.isMadeForKids ?? false}
                      onChange={(e) =>
                        setYtChannel(
                          produce((draft) => {
                            if (draft) draft.isMadeForKids = e.target.checked;
                          })
                        )
                      }
                    />
                  }
                  label={t("common:isMadeForKids")}
                />
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Typography color="text.primery" mb={0.5}>
            {t("modal:body.confirmYoutubeChannelDeletion")}
          </Typography>
        )}
      </Modal>
    );
  }
);

export default ModalCreateOrUpdateYoutubeChannel;
